// FILE: PullRequestRow.tsx
// Purpose: One pull request in the list — state glyph, title, where it lives, who opened it and
//          which branch it targets.
// Layer: Component
// Exports: PullRequestRow, PullRequestRowSkeleton
//
// No `+`/`−` here, deliberately. GitHub's list endpoint does not carry `additions`/`deletions` —
// only the single-pull-request endpoint does — so every row would need a request of its own to
// fill them in, which is the cost this section is built to avoid. The counts belong to the detail
// panel, where exactly one pull request has been asked for.
//
// The whole row is a button rather than a link: from the next phase on it opens the detail panel
// beside the list, not a new tab. The small anchor at the end is the actual link to GitHub, so
// copying and middle-clicking still behave like links.

import type { PullRequestListEntry } from "@taskin-code/contracts";
import { ArrowRightIcon, ExternalLinkIcon, GitBranchIcon } from "~/lib/icons";
import { Skeleton } from "~/components/ui/skeleton";
import { formatRelativeTime } from "~/lib/relativeTime";
import { cn } from "~/lib/utils";
import {
  PullRequestAuthorChip,
  PullRequestLabelDots,
  pullRequestMergeabilityDisplay,
  pullRequestStateDisplay,
} from "./pullRequestPresentation";

export function PullRequestRow({
  entry,
  selected = false,
  onSelect,
}: {
  entry: PullRequestListEntry;
  selected?: boolean;
  onSelect: (entry: PullRequestListEntry) => void;
}) {
  const state = pullRequestStateDisplay(entry);
  const mergeability = pullRequestMergeabilityDisplay(entry.mergeability);

  return (
    <div
      className={cn(
        "group relative flex items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors",
        selected
          ? "border-ring/50 bg-accent/50"
          : "border-border/60 bg-background/40 hover:bg-accent/30",
      )}
      data-slot="pull-request-row"
    >
      <button
        type="button"
        onClick={() => onSelect(entry)}
        // Stretched over the row so the whole card is the target, while the GitHub anchor below
        // stays above it and keeps its own link behaviour.
        className="absolute inset-0 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
        aria-label={`${state.label} pull request #${entry.number}: ${entry.title}`}
      />

      {/* The state is named in the row button's own label, so the glyph stays decorative. */}
      <state.Icon className={cn("mt-0.5 size-4 shrink-0", state.colorClass)} aria-hidden />

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
            {entry.title}
          </span>
          {mergeability ? (
            <span
              className={cn(
                "shrink-0 rounded-sm border px-1.5 py-px text-[10px] font-medium",
                mergeability.className,
              )}
            >
              {mergeability.label}
            </span>
          ) : null}
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
            {formatRelativeTime(entry.updatedAt)}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
          <span className="truncate font-medium text-muted-foreground/90">
            {entry.projectTitle}
          </span>
          <span aria-hidden>·</span>
          <span className="truncate">{entry.repository}</span>
          <span aria-hidden>·</span>
          <span className="tabular-nums">#{entry.number}</span>
          <span aria-hidden>·</span>
          <PullRequestAuthorChip author={entry.author} />
          <span aria-hidden>·</span>
          <span className="inline-flex min-w-0 items-center gap-1">
            <GitBranchIcon className="size-3 shrink-0 opacity-70" aria-hidden />
            <span className="truncate">{entry.headBranch}</span>
            <ArrowRightIcon className="size-3 shrink-0 opacity-50" aria-hidden />
            <span className="truncate">{entry.baseBranch}</span>
          </span>
          <PullRequestLabelDots labels={entry.labels} />
        </div>
      </div>

      <a
        href={entry.url}
        target="_blank"
        rel="noreferrer noopener"
        onClick={(event) => event.stopPropagation()}
        className="relative z-10 mt-0.5 shrink-0 rounded-sm p-1 text-muted-foreground/60 opacity-0 outline-none transition-opacity hover:text-foreground focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring/60 group-hover:opacity-100"
        aria-label={`Open #${entry.number} on GitHub`}
      >
        <ExternalLinkIcon className="size-3.5" aria-hidden />
      </a>
    </div>
  );
}

export function PullRequestRowSkeleton() {
  return <Skeleton className="h-[60px] w-full rounded-xl" />;
}
